import { apiGetDrive } from '@/api/firebase'
import router from '@/router'
import { getPathFromLocation, getUuid, RenderroS3Client } from '@/services/files'
import { routerPush } from '@/services/routing'
import { initRequestHelper } from '@/store/root'
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex'
import { Directory, File, FileState, FileSystemItem } from '../types/file'
import { RootState } from '../types/root'

const getDefaultState = (): FileState => {
  return {
    selectedDriveUuid: '',
    drive: null,
    // current level
    directories: [],
    files: [],
    selectedPaths: new Set(),
    path: '/',
  }
}

const moduleState = getDefaultState()

const moduleGetters: GetterTree<FileState, RootState> = {
  files(state) {
    return state.files
  },
  directories(state) {
    return state.directories
  },
  loadFilesRequest(_state, _getters, rootState) {
    return rootState.requests.loadFiles
  },
  directoryNavigateRequest(_state, _getters, rootState) {
    return rootState.requests.directoryNavigate
  },
  path(state) {
    return state.path
  },
  selectedDriveUuid(state) {
    return state.selectedDriveUuid
  },
  s3Info(state) {
    if (state.drive) {
      return {
        name: state.drive.name,
        s3Key: state.drive.s3.iam_user_access_key_id,
        s3Secret: state.drive.s3.iam_user_secret_access_key,
        bucket: state.drive.s3.bucket_name,
        region: state.drive.region,
      }
    }
    return null
  },
  s3Client(_state, getters): null | RenderroS3Client {
    if (!getters.s3Info) {
      return null
    }
    const info = getters.s3Info
    const region = info.region || 'eu-west-1'
    return new RenderroS3Client(info.s3Key, info.s3Secret, info.bucket, region)
  },
  selectedPaths(state) {
    return state.selectedPaths
  },
  selectedDirectories(state, getters): Directory[] {
    return getters.directories.filter((d: Directory) => state.selectedPaths.has(d.s3Path))
  },
  selectedFiles(state, getters): File[] {
    return getters.files.filter((f: File) => state.selectedPaths.has(f.s3Path))
  },
  selectedItems(_state, getters): FileSystemItem[] {
    return getters.selectedDirectories.concat(getters.selectedFiles)
    // .filter((i: FileSystemItem) => state.selectedPaths[i.s3Path])
  },
  singleSelectedItem(_state, getters): FileSystemItem | undefined {
    if (getters.selectedItems.length == 1) {
      return getters.selectedItems[0]
    }
    return undefined
  },
  numberOfSelected(state) {
    return state.selectedPaths.size
  },
}

const moduleActions: ActionTree<FileState, RootState> = {
  async goToDirectory({ state, dispatch, commit }, path = '/') {
    const [setSuccess, setFailure] = initRequestHelper(commit, 'directoryNavigate')
    try {
      await routerPush(router, `/drives/${state.selectedDriveUuid}/${path}`)
      await dispatch('loadDirectory')
      setSuccess()
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },
  async loadDirectory({ commit, getters }) {
    const pathMatch = router.currentRoute.params.pathMatch
    commit('selectDrive', { uuid: getUuid(pathMatch) })
    commit('clearSelection')
    const path = getPathFromLocation(pathMatch)
    commit('setPath', path)
    const [setSuccess, setFailure] = initRequestHelper(commit, 'loadFiles')
    try {
      const drive = await apiGetDrive(getUuid(pathMatch))
      commit('setDrive', drive)
      const { files, directories } = await getters.s3Client.loadDirectory(path)
      commit('setData', {
        files,
        directories,
      })

      setSuccess()
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },
  async clearSelection({ commit }) {
    commit('clearSelection')
  },
  async clearBrowser({ commit }) {
    commit('clearDriveSelection')
    commit('clearPath')
    commit('clearData')
  },
  async createDirectory({ commit, dispatch, getters }, name) {
    const [setSuccess, setFailure] = initRequestHelper(commit, 'directoryCreate')
    try {
      const path = getters.path
      await getters.s3Client.createDirectory(path, name)
      await dispatch('loadDirectory')
      setSuccess()
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  async deleteSelected({ commit, getters, dispatch }) {
    const [setSuccess, setFailure] = initRequestHelper(commit, 'itemsDelete')
    try {
      await getters.s3Client.deleteItems(getters.selectedItems)
      // await dispatch('clearSelection')
      await dispatch('loadDirectory')
      setSuccess()
    } catch (err: any) {
      // TODO Getting POST error 400 when deleting directory
      // looks like it's removed from S3 so right now I have to manually refresh
      setFailure(err.message)
      throw err
    }
  },

  async renameItem({ dispatch, commit, getters }, destPath) {
    const destPathS3 = destPath
    const [setSuccess, setFailure] = initRequestHelper(commit, 'itemRename')
    try {
      await getters.s3Client.moveItem(getters.singleSelectedItem, destPathS3)
      // await dispatch('clearSelection')
      await dispatch('loadDirectory')
      setSuccess()
    } catch (err: any) {
      // TODO Getting POST error 400 when deleting directory
      // looks like it's removed from S3 so right now I have to manually refresh
      // TODO when err is not a string
      // updateRequest does not get error message correctly
      // have to pass err.message instead
      setFailure(err.message)
      throw err
    }
  },

  async downloadMultiple({ commit, getters }) {
    const [setSuccess, setFailure] = initRequestHelper(commit, 'loadFiles')
    try {
      await getters.s3Client.downloadMultiple(getters.selectedPaths)
      setSuccess()
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  async getZip({ commit, getters }) {
    const [setSuccess, setFailure] = initRequestHelper(commit, 'loadFiles')
    try {
      await getters.s3Client.downloadZipArchive(getters.selectedDirectories, getters.selectedFiles)
      setSuccess()
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },
  async resetFileState({ commit }) {
    commit('resetState')
  },
}

const moduleMutations: MutationTree<FileState> = {
  selectDrive(state, { uuid }) {
    state.selectedDriveUuid = uuid
  },
  setDrive(state, drive) {
    state.drive = drive
  },
  clearDriveSelection(state) {
    state.selectedDriveUuid = ''
    state.drive = null
  },
  clearSelection(state) {
    state.selectedPaths = new Set()
  },
  selectPath(state, path) {
    const copy = new Set(state.selectedPaths)
    copy.add(path)
    state.selectedPaths = copy
  },
  deselectPath(state, path) {
    const copy = new Set(state.selectedPaths)
    copy.delete(path)
    state.selectedPaths = copy
  },
  selectMultiplePaths(state, paths: string[]): void {
    const copy = new Set(state.selectedPaths)
    paths.map((path: string) => copy.add(path))
    state.selectedPaths = copy
  },
  setPath(state, path) {
    state.path = path
  },
  clearPath(state) {
    state.path = '/'
  },
  setData(state, { files, directories }) {
    state.files = files
    state.directories = directories
  },
  clearData(state) {
    state.files = []
    state.directories = []
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  },
}

const file: Module<FileState, RootState> = {
  namespaced: true,
  state: moduleState,
  getters: moduleGetters,
  actions: moduleActions,
  mutations: moduleMutations,
}
export default file
